import { Injectable } from '@angular/core';
import { Auth, signInAnonymously } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { CommerceOrderStatus, PublicStorefront } from './shop.models';

@Injectable({ providedIn: 'root' })
export class ShopService {
  constructor(private auth: Auth, private functions: Functions) { }

  /** Ensures public shop calls use a persistent anonymous Firebase session. */
  async ensureAnonymousAuthentication(): Promise<void> {
    await this.auth.authStateReady();
    const user = this.auth.currentUser?.isAnonymous === true
      ? this.auth.currentUser
      : (await signInAnonymously(this.auth)).user;
    await user.getIdToken();
  }

  /** Loads a sanitized event storefront, optionally restricted to one public shop identifier. */
  async getStorefront(storefrontId: string, shopId: string): Promise<PublicStorefront> {
    const callable = httpsCallable<{ storefrontId: string; shopId?: string }, { success: boolean; storefront: PublicStorefront }>(this.functions, 'getPublicCommerceStorefront');
    const result = await callable({ storefrontId, ...(shopId ? { shopId } : {}) });
    return result.data.storefront;
  }

  /** Reserves authoritative stock for the current cart before payment starts. */
  async reserveOrder(input: {
    storefrontId: string;
    shopId?: string;
    language: string;
    customer: { firstName: string; lastName: string; email: string; emailConfirmation: string };
    cart: Array<{ shopId: string; itemId: string; quantity: number }>;
  }): Promise<{ orderId: string; transactionToken: string; expiresAt: number }> {
    const callable = httpsCallable<typeof input, { success: boolean; orderId: string; transactionToken: string; expiresAt: number }>(this.functions, 'reserveCommerceOrder');
    const result = await callable(input);
    return result.data;
  }

  /** Creates the single Mollie checkout for a previously reserved order. */
  async startPayment(storefrontId: string, orderId: string, currentUrl: string): Promise<{ completed: boolean; checkoutURL?: string; transactionToken: string }> {
    const callable = httpsCallable<{ storefrontId: string; orderId: string; currentUrl: string }, { success: boolean; completed: boolean; checkoutURL?: string; transactionToken: string }>(this.functions, 'startCommercePayment');
    const result = await callable({ storefrontId, orderId, currentUrl });
    return result.data;
  }

  /** Reads the payment result using the private redirect token. */
  async getOrderStatus(storefrontId: string, transactionToken: string): Promise<CommerceOrderStatus> {
    const callable = httpsCallable<{ storefrontId: string; transactionToken: string }, { success: boolean; order: CommerceOrderStatus }>(this.functions, 'getCommerceOrderStatus');
    const result = await callable({ storefrontId, transactionToken });
    return result.data.order;
  }

  /** Releases an order when the customer explicitly cancels Mollie checkout. */
  async cancelOrder(storefrontId: string, orderId: string): Promise<void> {
    const callable = httpsCallable<{ storefrontId: string; orderId: string }, { success: boolean }>(this.functions, 'cancelCommerceOrder');
    await callable({ storefrontId, orderId });
  }
}
